import type { GridEventListener, GridValidRowModel } from '@mui/x-data-grid-pro';
import { memo, useCallback, useEffect, useMemo, type FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useStore } from 'zustand';
import type { CustomGridColDef } from '../../services';
import { storeColMapper } from '../../services';
import type { SavedColDef } from '../../store';
import { buildColumnPipeline, isRealColumn, isSystemColumn, syncColumnsWithStore } from '../../utils';
import { useStoreHydration } from '../useStoreHydration';
import type { GridColumnsProps, UseGridColumnsConfig, UseGridColumnsReturn } from './types';

export const useGridColumns = <
  TRow extends GridValidRowModel,
  TFilterProps extends GridColumnsProps = GridColumnsProps,
>(
  config: UseGridColumnsConfig<TRow, TFilterProps>,
): UseGridColumnsReturn<TRow, TFilterProps> => {
  const {
    store,
    defaultColumns,
    FiltersComponent,
    initialFilter,
    paddingConfig,
    hasAsyncStorage = false,
    withSkeletonSupport = true,
  } = config;

  const storeColumns = useStore(
    store,
    useCallback((state) => state.currentColumnsSettings as SavedColDef[] | null, []),
  );

  const saveColumns = useStore(
    store,
    useCallback((state) => state.saveColumns, []),
  );

  const isHydrated = useStoreHydration(store, hasAsyncStorage);

  const location = useLocation();

  const categorizedDefaults = useMemo(() => {
    const real: CustomGridColDef<TRow>[] = [];
    const system: CustomGridColDef<TRow>[] = [];
    const cannotBeHidden: CustomGridColDef<TRow>[] = [];

    for (const col of defaultColumns) {
      if (isRealColumn(col)) {
        real.push(col);
      }
      if (isSystemColumn(col)) {
        system.push(col);
      }
      if (col.cannotBeHidden) {
        cannotBeHidden.push(col);
      }
    }

    return { real, system, cannotBeHidden };
  }, [defaultColumns]);

  useEffect(() => {
    const columnsToSave = syncColumnsWithStore(storeColumns, categorizedDefaults.real, isHydrated, hasAsyncStorage);

    if (columnsToSave) {
      saveColumns(columnsToSave as SavedColDef[] & unknown);
    }
  }, [storeColumns, categorizedDefaults.real, isHydrated, hasAsyncStorage, saveColumns]);

  const processedData = useMemo(
    () =>
      buildColumnPipeline({
        storeColumns,
        realDefaultColumns: categorizedDefaults.real,
        systemColumns: categorizedDefaults.system,
        defaultColumns,
        cannotBeHiddenDefaults: categorizedDefaults.cannotBeHidden,
        withSkeletonSupport,
        paddingConfig,
      }),
    [storeColumns, categorizedDefaults, defaultColumns, withSkeletonSupport, paddingConfig],
  );

  const FiltersWithStore = useMemo(() => {
    if (!FiltersComponent) {
      return undefined;
    }

    const WrappedFilters = memo((props: Omit<TFilterProps, 'store' | 'columns' | 'defaultColumns'>) => (
      <FiltersComponent
        {...(props as TFilterProps)}
        columns={processedData.columnsForFilters}
        defaultColumns={defaultColumns}
        store={store}
      />
    ));

    WrappedFilters.displayName = `FiltersWithStore(${FiltersComponent.displayName || FiltersComponent.name || 'Component'})`;

    return WrappedFilters as unknown as FC<Omit<TFilterProps, 'store' | 'columns' | 'defaultColumns'>>;
  }, [FiltersComponent, store, processedData.columnsForFilters, defaultColumns]);

  const defaultFilter = useStore(store, (state) => {
    if (location.search) {
      return {};
    }

    const defaultView = state.settings.find((setting) => setting.isDefault);
    if (defaultView?.filters) {
      return defaultView.filters;
    }

    return initialFilter ?? {};
  });

  const onColumnWidthChange = useCallback<GridEventListener<'columnWidthChange'>>(
    ({ colDef }) => {
      const columnsToSave = processedData.columnsForFilters.map((column) => {
        if (column.field === colDef.field) {
          return { ...column, width: colDef.width };
        }
        return column;
      });

      saveColumns(storeColMapper(columnsToSave) as SavedColDef[] & unknown);
    },
    [processedData.columnsForFilters, saveColumns],
  );

  const onColumnOrderChange = useCallback<GridEventListener<'columnOrderChange'>>(
    (params) => {
      const currentColumns = [...processedData.columnsForFilters];

      // 1. Skopiuj visibleColumns i wykonaj na nich tę samą operację co MUI
      const reorderedVisible = [...processedData.visibleColumns];
      const [movedCol] = reorderedVisible.splice(params.oldIndex, 1);
      reorderedVisible.splice(params.targetIndex, 0, movedCol);

      const visibleFields = new Set(reorderedVisible.map((column) => String(column.field)));

      // Budujemy mapę: dla każdej ukrytej kolumny zapisujemy kolumnę widoczną, która była przed nią
      const hiddenColsContext = new Map<string, string | null>();
      let lastVisibleField: string | null = null;

      currentColumns.forEach((col) => {
        const field = String(col.field);
        if (visibleFields.has(field)) {
          lastVisibleField = field;
        } else {
          // To jest ukryta kolumna - zapamiętaj kto był przed nią
          hiddenColsContext.set(field, lastVisibleField);
        }
      });

      // 3. Budujemy nową kolejność: wstawiamy widoczne w nowej kolejności,
      // a ukryte wstawiamy tam gdzie były (relatywnie do widocznych)
      const newOrder: typeof currentColumns = [];

      reorderedVisible.forEach((visibleCol) => {
        const field = String(visibleCol.field);

        // Znajdź wszystkie ukryte kolumny które były PRZED tą widoczną
        currentColumns.forEach((col) => {
          const colField = String(col.field);
          if (!visibleFields.has(colField) && hiddenColsContext.get(colField) === field) {
            // Ta ukryta kolumna była po "field", ale przed następną widoczną
            // Dodajemy ją teraz, przed dodaniem "field"
            newOrder.push(col);
          }
        });

        // Dodaj widoczną kolumnę
        const fullCol = currentColumns.find((c) => c.field === visibleCol.field);
        if (fullCol) {
          newOrder.push(fullCol);
        }
      });

      // Dodaj ukryte kolumny które były na końcu (po ostatniej widocznej)
      currentColumns.forEach((col) => {
        const field = String(col.field);
        if (!visibleFields.has(field) && !newOrder.some((c) => c.field === col.field)) {
          newOrder.push(col);
        }
      });

      saveColumns(storeColMapper(newOrder) as SavedColDef[] & unknown);
    },
    [processedData.columnsForFilters, processedData.visibleColumns, saveColumns],
  );

  return useMemo(() => {
    return {
      visibleColumns: processedData.visibleColumns,
      allColumns: processedData.allColumns,
      pinnedColumns: processedData.pinnedColumns,
      defaultSort: processedData.defaultSort,
      defaultFilter,
      FiltersWithStore,
      handlers: {
        onColumnWidthChange,
        onColumnOrderChange,
      },
    };
  }, [
    processedData.visibleColumns,
    processedData.allColumns,
    processedData.pinnedColumns,
    processedData.defaultSort,
    defaultFilter,
    FiltersWithStore,
    onColumnWidthChange,
    onColumnOrderChange,
  ]);
};
